import {
  Box,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { useVettingApplicationDetailQuery } from '../../api/use-vetting-application-detail-query';
import { useVettingConfigQuery } from '../../api/use-vetting-config-query';
import { useOverrideVettingDecisionMutation } from '../../api/use-override-vetting-decision-mutation';
import { useRescoreVettingApplicationMutation } from '../../api/use-rescore-vetting-application-mutation';
import { formatVettingDateTime } from '../../services/vettingApplications';
import { getVettingAutoRejectRuleLabel } from '../../constants/vettingAutoRejectRules';
import { getVettingSpamFlagReasonLabel } from '../../constants/vettingSpamFlagReasons';
import PageContainer from '../PageContainer';
import QuestionList from '../UserDetails/Sections/QuestionList';
import VideoSection from '../UserDetails/Sections/Video';
import { DecisionThresholdBar, getVettingThresholds } from './vettingDecisionDisplay';

const VETTED_COMMUNITY_USER_QUESTIONS = [
  'What made you apply to SureSpace today, and what are you hoping will be different in your life six months from now?',
  'Tell me about a time you stepped outside your comfort zone to meet new people. What happened, and how did it make you feel?',
  "Imagine you've joined SureSpace. What would an ideal month look like for you? How often would you expect to attend events or meet new people?",
  'Communities are only as strong as the people in them. What do you think you would contribute to the SureSpace community?',
  "SureSpace is a paid membership with a strong expectation that members actively participate and attend events. Why do you think that model would — or wouldn't — work for you?",
];

const DECISION_OPTIONS = ['accepted', 'waitlisted', 'rejected'];

const panelSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
  border: '1px solid',
  borderColor: 'rgba(47, 42, 38, 0.1)',
  borderRadius: 2.5,
  bgcolor: 'background.paper',
  p: { xs: 1.75, md: 2.25 },
  minWidth: 0,
  width: '100%',
};

const BandSection = ({ step, title, description, children }) => (
  <Box
    sx={{
      width: '100%',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 3,
      bgcolor: 'background.default',
      p: { xs: 2, md: 2.75 },
      display: 'flex',
      flexDirection: 'column',
      gap: 2.25,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '8px',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.8rem',
          fontWeight: 800,
          flexShrink: 0,
          mt: 0.15,
        }}
      >
        {step}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.25 }}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
            {description}
          </Typography>
        )}
      </Box>
    </Box>
    {children}
  </Box>
);

const splitRowSx = {
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  gap: { xs: 1.75, md: 2.25 },
  width: '100%',
  alignItems: 'stretch',
};

const Panel = ({ title, children, sx = {} }) => (
  <Box sx={{ ...panelSx, ...sx }}>
    {title && (
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
        {title}
      </Typography>
    )}
    {children}
  </Box>
);

const DetailRow = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.2 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere', mt: 0.25 }}>
      {value ?? '-'}
    </Typography>
  </Box>
);

const ScoreTile = ({ label, value }) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'rgba(47, 42, 38, 0.08)',
      borderRadius: 2,
      bgcolor: 'action.hover',
      px: 1.5,
      py: 1.5,
      textAlign: 'center',
      minWidth: 0,
      flex: { xs: '1 1 calc(50% - 0.625rem)', sm: '1 1 calc(33.333% - 0.84rem)', md: 1 },
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignItems: 'center',
      minHeight: { xs: 76, md: 84 },
      gap: 1,
    }}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        display: 'block',
        fontWeight: 600,
        lineHeight: 1.3,
        minHeight: { xs: '1.3em', md: '2.6em' },
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="h5"
      sx={{
        fontWeight: 800,
        color: 'text.primary',
        lineHeight: 1,
        fontSize: { xs: '1.25rem', md: '1.5rem' },
      }}
    >
      {value ?? '-'}
    </Typography>
  </Box>
);

const ScoreRow = ({ children }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      alignItems: 'stretch',
      gap: 1.25,
      width: '100%',
    }}
  >
    {children}
  </Box>
);

const getQuestionLabel = (response) => {
  const questionNumber = Number(response?.questionNumber);
  return VETTED_COMMUNITY_USER_QUESTIONS[questionNumber - 1] || `Question ${questionNumber || ''}`;
};

const VettedCommunityUserDetails = () => {
  const { id } = useParams();
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);
  const [overrideDecision, setOverrideDecision] = useState('accepted');
  const [overrideReason, setOverrideReason] = useState('');
  const { data, isLoading, error } = useVettingApplicationDetailQuery(id);
  const { data: configData } = useVettingConfigQuery();
  const rescoreMutation = useRescoreVettingApplicationMutation();
  const overrideDecisionMutation = useOverrideVettingDecisionMutation();

  const thresholds = useMemo(() => getVettingThresholds(configData?.active), [configData?.active]);

  const application = data?.application;
  const responses = useMemo(() => data?.responses ?? [], [data?.responses]);
  const currentQuestionData = responses[selectedQuestionIndex] ?? null;
  const dimensionSummary = application?.dimensionScoreSummary ?? {};
  const autoRejectRule = application?.autoRejectRule;
  const spamFlagged = Boolean(application?.spamFlagged);
  const spamFlagReasonLabel = spamFlagged
    ? getVettingSpamFlagReasonLabel(application?.spamFlagReason)
    : null;

  const questions = useMemo(() => responses.map(getQuestionLabel), [responses]);

  const handleQuestionChange = useCallback((index) => {
    setSelectedQuestionIndex(index);
  }, []);

  const handleRescore = useCallback(async () => {
    if (!id) return;

    try {
      const result = await rescoreMutation.mutateAsync({ applicationId: id });
      toast.success(result.message || 'Rescore triggered');
    } catch (rescoreError) {
      toast.error(rescoreError.message || 'Failed to trigger rescore');
    }
  }, [id, rescoreMutation]);

  const handleOverrideDecision = useCallback(async () => {
    if (!id) return;

    const trimmedReason = overrideReason.trim();
    if (!trimmedReason) {
      toast.error('Reason is required');
      return;
    }

    try {
      const result = await overrideDecisionMutation.mutateAsync({
        applicationId: id,
        decision: overrideDecision,
        reason: trimmedReason,
      });
      toast.success(result.message || 'Decision overridden successfully');
      setOverrideReason('');
    } catch (overrideError) {
      toast.error(overrideError.message || 'Failed to override decision');
    }
  }, [id, overrideDecision, overrideReason, overrideDecisionMutation]);

  return (
    <PageContainer title="Vetted Community User Details">
      {isLoading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress sx={{ color: 'primary.main' }} />
        </Box>
      )}

      {error && (
        <Typography color="error" variant="body2">
          {error.message}
        </Typography>
      )}

      {!isLoading && !error && !application && (
        <Typography variant="body2" color="text.secondary">
          Application not found.
        </Typography>
      )}

      {!isLoading && !error && application && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 3, sm: 4, md: 6.75 },
            width: '100%',
            minWidth: 0,
          }}
        >
          <BandSection
            step={1}
            title="Question Details"
            description="Review the video response, transcript, and question-level scores."
          >
            <Box sx={splitRowSx}>
              <Panel sx={{ flex: { md: 1.15 }, p: 1.25 }}>
                <Box sx={{ height: { xs: 220, sm: 280, md: 320 } }}>
                  <VideoSection videoUrl={currentQuestionData?.videoUrl} />
                </Box>
              </Panel>

              <Panel sx={{ flex: { md: 1 } }}>
                {questions.length > 0 ? (
                  <QuestionList
                    questions={questions}
                    selectedQuestionIndex={selectedQuestionIndex}
                    onQuestionChange={handleQuestionChange}
                  />
                ) : (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Question List
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      No responses available.
                    </Typography>
                  </>
                )}
              </Panel>
            </Box>

            {currentQuestionData ? (
              <Box sx={splitRowSx}>
                <Panel title="Transcript" sx={{ flex: { md: 1.15 } }}>
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere', lineHeight: 1.7 }}>
                    {currentQuestionData.transcriptText || 'No transcript available.'}
                  </Typography>
                </Panel>

                <Panel title="Question Scores" sx={{ flex: { md: 1 } }}>
                  <ScoreRow>
                    <ScoreTile label="Sentiment" value={currentQuestionData.sentimentScore} />
                    <ScoreTile
                      label="Keyword Match"
                      value={currentQuestionData.keywordMatchScore}
                    />
                    <ScoreTile label="Specificity" value={currentQuestionData.specificityScore} />
                    <ScoreTile
                      label="Weighted Score"
                      value={currentQuestionData.weightedQuestionScore}
                    />
                    <ScoreTile label="Word Count" value={currentQuestionData.transcriptWordCount} />
                  </ScoreRow>
                </Panel>
              </Box>
            ) : (
              <Panel title="No Response Selected">
                <Typography variant="body2" color="text.secondary">
                  Select a response to view scoring details.
                </Typography>
              </Panel>
            )}
          </BandSection>

          <BandSection
            step={2}
            title="Overall Summary"
            description="See the final decision status and aggregated dimension scores for the full application."
          >
            <Box sx={splitRowSx}>
              <Panel title="Decision & Score" sx={{ flex: { md: 1.15 } }}>
                <DecisionThresholdBar
                  score={application.totalScore}
                  decision={application.decision}
                  thresholds={thresholds}
                />
              </Panel>

              <Panel title="Overall Score Summary" sx={{ flex: { md: 1 } }}>
                <Typography variant="caption" color="text.secondary" sx={{ mt: -0.5 }}>
                  Aggregated across all responses
                </Typography>
                <ScoreRow>
                  <ScoreTile label="Sentiment" value={dimensionSummary.sentiment} />
                  <ScoreTile label="Keyword Match" value={dimensionSummary.keywordMatch} />
                  <ScoreTile label="Specificity" value={dimensionSummary.specificity} />
                </ScoreRow>
              </Panel>
            </Box>

            {(autoRejectRule || spamFlagged) && (
              <Box sx={splitRowSx}>
                {autoRejectRule && (
                  <Panel title="Auto Reject Rule" sx={{ flex: { md: 1 } }}>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'rgba(211, 47, 47, 0.28)',
                        bgcolor: 'rgba(211, 47, 47, 0.06)',
                        borderRadius: 2,
                        px: 1.75,
                        py: 1.5,
                        height: '100%',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main' }}>
                        {getVettingAutoRejectRuleLabel(autoRejectRule)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5 }}
                      >
                        Rule code: {autoRejectRule}
                      </Typography>
                    </Box>
                  </Panel>
                )}

                {spamFlagged && (
                  <Panel title="Spam Flag" sx={{ flex: { md: 1 } }}>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'rgba(211, 47, 47, 0.28)',
                        bgcolor: 'rgba(211, 47, 47, 0.06)',
                        borderRadius: 2,
                        px: 1.75,
                        py: 1.5,
                        height: '100%',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main' }}>
                        {spamFlagReasonLabel || 'Flagged as spam'}
                      </Typography>
                    </Box>
                  </Panel>
                )}
              </Box>
            )}
          </BandSection>

          <BandSection
            step={3}
            title="Applicant Details & Moderation"
            description="Confirm applicant identity and apply rescore or decision override actions."
          >
            <Box sx={splitRowSx}>
              <Panel title="Applicant Details" sx={{ flex: { md: 1 } }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(0, 1fr))',
                    },
                    gap: 2,
                  }}
                >
                  <DetailRow label="Applicant" value={application.fullName} />
                  <DetailRow label="Email" value={application.userEmail} />
                  <DetailRow label="City" value={application.city} />
                  <DetailRow
                    label="Submitted"
                    value={formatVettingDateTime(application.createdAt)}
                  />
                  <DetailRow label="Decided" value={formatVettingDateTime(application.decidedAt)} />
                  <DetailRow label="Scored" value={formatVettingDateTime(application.scoredAt)} />
                </Box>
              </Panel>

              <Panel title="Manual Moderation Actions" sx={{ flex: { md: 1.15 }, gap: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    gap: 1.5,
                    alignItems: { xs: 'stretch', sm: 'center' },
                    bgcolor: 'action.hover',
                    borderRadius: 2,
                    px: 1.5,
                    py: 1.25,
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Rescore Application
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Retry scoring for failed or stale results.
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={handleRescore}
                    disabled={rescoreMutation.isPending}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      alignSelf: { xs: 'stretch', sm: 'center' },
                    }}
                  >
                    {rescoreMutation.isPending ? 'Triggering...' : 'Rescore'}
                  </Button>
                </Box>

                <Divider />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Override Decision
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Reason is required for every manual override.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 1.5,
                      alignItems: { xs: 'stretch', sm: 'flex-start' },
                      flexWrap: 'wrap',
                    }}
                  >
                    <TextField
                      select
                      label="Decision"
                      value={overrideDecision}
                      onChange={(event) => setOverrideDecision(event.target.value)}
                      size="small"
                      fullWidth
                      sx={{ minWidth: { sm: 140 }, flex: { sm: '0 1 160px' } }}
                    >
                      {DECISION_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label="Reason"
                      value={overrideReason}
                      onChange={(event) => setOverrideReason(event.target.value)}
                      placeholder="Explain why this manual override is needed"
                      size="small"
                      multiline
                      minRows={2}
                      fullWidth
                      sx={{ flex: { sm: '1 1 220px' }, minWidth: { sm: 180 } }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleOverrideDecision}
                      disabled={overrideDecisionMutation.isPending}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        bgcolor: 'primary.main',
                        whiteSpace: 'nowrap',
                        width: { xs: '100%', sm: 'auto' },
                        '&:hover': { bgcolor: 'primary.dark' },
                      }}
                    >
                      {overrideDecisionMutation.isPending ? 'Saving...' : 'Apply Override'}
                    </Button>
                  </Box>
                </Box>
              </Panel>
            </Box>
          </BandSection>
        </Box>
      )}
    </PageContainer>
  );
};

export default VettedCommunityUserDetails;
